import { Component, ChangeDetectionStrategy, ElementRef, input, signal, computed, inject, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { AppSettings } from '@services/settings/app-settings.service';
import { BottomSheetComponent } from '../bottom-sheet/bottom-sheet.component';

@Component({
  selector: 'app-sheet-select',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, BottomSheetComponent],
  templateUrl: './sheet-select.component.html',
  styleUrl: './sheet-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SheetSelectComponent),
    multi: true
  }]
})
export class SheetSelectComponent implements ControlValueAccessor {
  readonly appSettings = inject(AppSettings);
  private host = inject(ElementRef<HTMLElement>);

  items = input<any[]>([]);
  bindLabel = input('name');
  bindValue = input<string | null>(null);
  placeholder = input('Seleccione...');
  title = input('');
  clearable = input(false);
  searchable = input(false);
  readonly = input(false);

  open = signal(false);
  inlineOpen = signal(false);
  query = signal('');
  disabled = signal(false);
  value = signal<any>(null);

  isNested(): boolean {
    return !!this.host.nativeElement.closest('.bottom-sheet');
  }

  filteredItems = computed(() => {
    const list = this.items() ?? [];
    const q = this.query().trim().toLowerCase();
    if (!q) return list;
    return list.filter((item) => this.labelOf(item).toLowerCase().includes(q));
  });

  displayLabel = computed(() => {
    const current = this.value();
    if (current == null || current === '') return '';
    const key = this.bindValue();
    const match = (this.items() ?? []).find((item) =>
      key ? item?.[key] === current : item === current
    );
    return match ? this.labelOf(match) : String(current);
  });

  private onChange: (value: any) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  labelOf(item: any): string {
    if (item == null) return '';
    if (typeof item !== 'object') return String(item);
    return String(item[this.bindLabel()] ?? '');
  }

  valueOf(item: any): any {
    const key = this.bindValue();
    return key ? item?.[key] : item;
  }

  isSelected(item: any): boolean {
    return this.valueOf(item) === this.value();
  }

  writeValue(value: any): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  openSheet(): void {
    if (this.disabled() || this.readonly()) return;
    this.query.set('');
    if (this.isNested()) {
      this.inlineOpen.update((open) => !open);
      return;
    }
    this.open.set(true);
  }

  pick(item: any): void {
    const next = this.valueOf(item);
    this.value.set(next);
    this.onChange(next);
    this.onTouched();
    this.open.set(false);
    this.inlineOpen.set(false);
  }

  clear(): void {
    this.value.set(null);
    this.onChange(null);
    this.onTouched();
    this.open.set(false);
    this.inlineOpen.set(false);
  }

  onDesktopChange(value: any): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }
}
